import { AxiosResponse } from 'axios';
import { apiClient } from '../../apiHelper';
import {
  QueryKeyT,
  useFetch_old,
  useFetch,
  usePost_old,
  usePost,
  usePaginated,
  useGenericMutation,
  useCreate,
  useEdit,
} from '../react-query';
import * as url from '../../urlHelper';
import { instanceReportQueryKeys } from './instance-report-query-keys';
import { instanceQueryKeys } from './instance-query-keys';
import { DataInfoPrediction } from './interface';

// TypeScript interfaces for instance data
export interface InstanceData {
  sop_instance_uid: string;
  serie_instance_uid?: string;
  study_instance_uid?: string;
  sop_class_uid?: string;
  index?: number | null;
  frames?: number;
  modality?: string;
  pixel_spacing?: number[];
  transfer_syntax?: string;
  prediction_state?: 'NOT_PREDICTED' | 'PREDICTED' | 'FAILED';
  format_file?: 'DICOM' | 'VIDEO';
  bucket_path?: string | null;
  job_id?: string | null;
  prediction?: any | null;
  status?: 'PROCESSING' | 'COMPLETED' | 'FAILED';
  referenced_sop_uids?: any[] | null;
  [key: string]: unknown;
}

export interface InstanceFramePixelSpacingUpdateData {
  sop_instance_uid: string;
  study_instance_uid: string;
  frame_index: number;
  pixel_spacing_value: number;
}

export const useAddSRDicom = (
  updater?: (oldData: any[], newData: any) => any[],
  onSuccess?: (response: unknown, data: any) => void,
  onError?: (error: Error, data: any) => void
) => usePost_old('instance', undefined, updater, onSuccess, onError);

// export const useGetSrReport = (
//     instance_uid: string,
//     queryKey?: QueryKeyT
// ) => {
//     console.log('useGetSrReport', instance_uid);
//     // console.log('useGetSrReport', instance_uid);
//     // if (instance_uid === undefined) {
//     //     return {  };
//     // }
//     // if ( instance_uid?.length === 0) {
//     //     return {  };
//     // }
//     return useFetch_old(`/instance_report/${instance_uid}`, undefined, { enabled: !!instance_uid }, queryKey);
// };

export const useGetSrReport = (instance_uid: string, queryKey?: QueryKeyT) => {
  console.log('useGetSrReport', instance_uid);
  // console.log('useGetSrReport', instance_uid);
  // if (instance_uid === undefined) {
  //     return {  };
  // }
  // if ( instance_uid?.length === 0) {
  //     return {  };
  // }
  return useFetch_old(
    `/instance_report/${instance_uid}`,
    undefined,
    {
      enabled: !!instance_uid,
      refetch: true,
      refetchOnWindowFocus: false,
    },
    queryKey
  );
};

// export const usePrediction = (
//     updater?: (oldData: any[], newData: any) => any[],
//     onSuccess?: (response: unknown, data: any) => void,
//     onError?: (error: Error, data: any) => void) => usePost_old('/model_api/predictInstances', undefined, updater, onSuccess, onError);

export const usePrediction = (
  onSuccess?: (response: unknown, data: any) => void,
  onError?: (error: Error, data: any) => void
) => usePost('/predictInstances', undefined, undefined, onSuccess, onError);

export const usePredictSerie = (
  onSuccess?: (response: unknown, data: any) => void,
  onError?: (error: Error, data: any) => void
) => usePost('/predictInstances', undefined, undefined, onSuccess, onError);

export function usePaginatedInstanceReport(
  page: number,
  itemsPerPage: number,
  studyUID: string
) {
  const getPaginatedExamsFn = async (p: number = page): Promise<any> => {
    const url = `instance_report/list/${studyUID}?page=${p}&items_per_page=${itemsPerPage}`;

    const response = await apiClient.get(url);
    return response.data;
  };

  return usePaginated(
    getPaginatedExamsFn,
    instanceReportQueryKeys.pagination(page),
    page
  );
}

export function useGetInstanceSrReport(
  onSuccess?: (response: any, data: any) => void,
  onError?: (error: any, data: any) => void
) {
  const getInstanceSrReportFn = async (data: {
    sop_instance_uid: string;
    study_uid: string;
  }): Promise<any> => {
    const url = `/instance_report/report/${data.study_uid}/${data.sop_instance_uid}`;
    const response = await apiClient.get(url);
    return response;
  };
  // return useFetch(getInstanceSrReportFn, undefined, { enabled: !!instanceUID });
  return useGenericMutation(
    getInstanceSrReportFn,
    ['instance_report'],
    'Error getting instance report',
    undefined,
    () => {},
    onSuccess,
    onError
  );
}

export function useCreateBlindInstance(
  onSuccess?: (response: AxiosResponse, data: any) => void,
  onError?: (responce: AxiosResponse, error: Error, index: Number) => void,
  notification: boolean = true
) {
  const createBlindInstance = async (data: any) => {
    try {
      const response = await apiClient.post(
        `/bs_tmp/upload_sweep/${data.study_instance_uid}?index=${data.index}&pixel_spacing=[${data.pixel_spacing},${data.pixel_spacing}]`,
        data.body
      );
      return response;
    } catch (error: any) {
      // Allow the onError callback to receive the index
      if (onError) {
        const response = error?.response || null;
        onError(response, error, data.index);
      }
      throw error; // rethrow to let useCreate handle it too, if needed
    }
  };

  return useCreate(
    createBlindInstance,
    ['create_blind_instance'],
    'Failed to create Blind Instance',
    'Blind Instance created successfully',
    onSuccess,
    onError,
    notification
  );
}

export function usePredictionBlind(
  onSuccess?: (response: unknown, data: any) => void,
  onError?: (error: Error, data: any) => void
) {
  const predictBlindStudy = async (data: { study_uid: string }) => {
    const response = await apiClient.post('/model_api/predictBlindStudy', data);
    return response;
  };

  return useCreate(
    predictBlindStudy,
    ['predict_blind_study'],
    'Failed to predict blind study',
    'Blind study predicted successfully',
    onSuccess,
    onError
  );
}

export function useRefreshBlindSweepUrls(
  StudyUID: string,
  queryKey?: QueryKeyT
) {
  return useFetch_old(
    `/instance/refresh_blind_sweep_urls/${StudyUID}`,
    undefined,
    {
      enabled: !!StudyUID,
      refetchOnWindowFocus: false,
    },
    queryKey
  );
}

// export function useGetGlobalReport(StudyUID: string, queryKey?: QueryKeyT) {
//     console.log('==== Fetcher useGetGlobalReport called with StudyUID:', StudyUID);
//     return useFetch_old(`${url.GET_STATE_REPORT}/${StudyUID}`, undefined, {
//       enabled: !!StudyUID,
//       refetch: true,
//       refetchOnWindowFocus: false
//     }, queryKey);
//   }

// export const usePredictionBlind = (
//     onSuccess?: (response: unknown, data: any) => void,
//     onError?: (error: Error, data: any) => void) => usePost('/model_api/predictBlindStudy', undefined, undefined, onSuccess, onError);

export const useAddInstanceToPregnancy = (
  updater: (oldData: any[], newData: any) => any[],
  onSuccess?: (response: any, data: any) => void,
  onError?: (error: any, data: any) => void
) => {
  return usePost<any[], any>(
    url.INSTANCE_ADD_TO_PREGNANCY,
    undefined,
    updater,
    onSuccess,
    onError
  );
};

export function useUpdateInstance(
  onSuccess?: (response: AxiosResponse, data: InstanceData) => void,
  onError?: (response: AxiosResponse, error: Error) => void
) {
  const updateInstance = async (
    data: InstanceData & { sop_instance_uid: string }
  ) => {
    const response = await apiClient.put(
      `/instance/${data.sop_instance_uid}`,
      data
    );
    return response.data;
  };

  return useEdit(
    updateInstance,
    instanceQueryKeys.all,
    'Failed to update instance',
    'Instance updated successfully',
    onSuccess,
    onError
  );
}

export function useUpdateInstanceFramePixelSpacing(
  onSuccess?: (
    response: AxiosResponse,
    data: InstanceFramePixelSpacingUpdateData
  ) => void,
  onError?: (response: AxiosResponse, error: Error) => void
) {
  const updateInstanceFramePixelSpacing = async (
    data: InstanceFramePixelSpacingUpdateData
  ) => {
    const response = await apiClient.put(
      `/instance/${data.sop_instance_uid}/pixel-spacing/frame`,
      {
        study_instance_uid: data.study_instance_uid,
        frame_index: data.frame_index,
        pixel_spacing_value: data.pixel_spacing_value,
      }
    );
    return response.data;
  };

  return useEdit(
    updateInstanceFramePixelSpacing,
    instanceQueryKeys.all,
    'Failed to update frame pixel spacing',
    'Frame pixel spacing updated successfully',
    onSuccess,
    onError
  );
}
